import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import {
  getSupportAgentContactCapabilities,
  getSupportAgentWhatsappThread,
  prepareSupportAgentEmail,
  sendSupportAgentWhatsappMessage,
  sendSupportAgentWhatsappTemplate,
} from 'src/supportPortal/services/supportAgentContactService'

const DEFAULT_EVENT_TYPE_OPTIONS = EVENT_TYPES_ARRAY.filter((option) => option?.id).map((option) => ({
  value: option.id,
  label: option.title || option.id,
}))

function normalizeEventTypeOptions(options = []) {
  if (!Array.isArray(options) || options.length === 0) {
    return DEFAULT_EVENT_TYPE_OPTIONS
  }

  return options
    .map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option }
      }
      return {
        value: option?.value || option?.id || '',
        label: option?.label || option?.title || option?.value || option?.id || '',
      }
    })
    .filter((option) => option.value)
}

function formatThreadTimestamp(value) {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function getTemplateActionChipColor(actionType) {
  if (actionType === 'call-heads-up') {
    return 'warning'
  }
  if (actionType === 'permission-template') {
    return 'info'
  }
  return 'default'
}

function getTemplateActionFriendlyLabel(actionType, fallbackLabel) {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (actionType === 'permission-template') {
    return 'Conversation opener'
  }
  if (actionType === 'call-heads-up') {
    return 'Call heads-up'
  }
  if (actionType === 'inbound-reply') {
    return 'Reply'
  }
  if (actionType === 'free-form') {
    return 'Message'
  }
  return 'Interaction'
}

function MessageBubble({ item }) {
  const incoming = item?.direction === 'incoming'
  const label = getTemplateActionFriendlyLabel(item?.actionType, item?.label)
  const statusLabel = item?.status ? String(item.status).toLowerCase() : ''

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: incoming ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        sx={{
          maxWidth: '86%',
          px: 1.5,
          py: 1.2,
          borderRadius: 2.5,
          background: incoming
            ? 'rgba(241, 245, 249, 0.96)'
            : 'linear-gradient(135deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.95) 100%)',
          border: incoming
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(22, 163, 74, 0.18)',
          boxShadow: incoming
            ? '0 8px 24px rgba(15, 23, 42, 0.05)'
            : '0 10px 26px rgba(22, 163, 74, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {incoming ? item?.targetDisplayName || 'Recipient' : item?.supportAgentName || 'Support'}
            </Typography>
            <Chip
              size="small"
              label={label}
              color={getTemplateActionChipColor(item?.actionType)}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatThreadTimestamp(item?.createdAt)}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: '#111827',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.55,
          }}
        >
          {item?.messagePreview || 'No message preview was captured for this interaction.'}
        </Typography>

        {statusLabel ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Status: {statusLabel.replaceAll('-', ' ')}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

export default function SupportContactActions({
  patientId,
  practitionerId,
  bookedEventId,
  clinicBrandId,
  clinicBrandName,
  clinicId,
  targetRole,
  eventType,
  eventTypesCsv,
  eventTypeOptions,
  uiSurface = 'support-contact-actions',
  title = 'Contact Actions',
  compact = false,
  sx = {},
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const normalizedEventTypeOptions = useMemo(
    () => normalizeEventTypeOptions(eventTypeOptions),
    [eventTypeOptions],
  )
  const [selectedEventType, setSelectedEventType] = useState(
    eventType || normalizedEventTypeOptions[0]?.value || '',
  )
  const [loading, setLoading] = useState(false)
  const [capabilities, setCapabilities] = useState(null)
  const [capabilitiesError, setCapabilitiesError] = useState('')
  const [whatsappDrawerOpen, setWhatsappDrawerOpen] = useState(false)
  const [threadLoading, setThreadLoading] = useState(false)
  const [threadError, setThreadError] = useState('')
  const [threadState, setThreadState] = useState({
    messages: [],
    templateActions: [],
    freeFormAllowed: false,
    targetDisplayName: '',
  })
  const [selectedTemplateAction, setSelectedTemplateAction] = useState('')
  const [messageDraft, setMessageDraft] = useState('')
  const [messageSending, setMessageSending] = useState(false)
  const [templateSending, setTemplateSending] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)

  useEffect(() => {
    if (eventType) {
      setSelectedEventType(eventType)
      return
    }
    if (!selectedEventType && normalizedEventTypeOptions.length === 1) {
      setSelectedEventType(normalizedEventTypeOptions[0].value)
    }
  }, [eventType, normalizedEventTypeOptions, selectedEventType])

  const resolvedEventType = eventType || selectedEventType || ''
  const requiresEventTypeSelection = !eventTypesCsv && !resolvedEventType

  const context = useMemo(
    () => ({
      patientId,
      practitionerId,
      bookedEventId,
      clinicBrandId,
      clinicBrandName,
      clinicId,
      targetRole,
      eventType: resolvedEventType || undefined,
      eventTypesCsv: eventTypesCsv || undefined,
    }),
    [
      bookedEventId,
      clinicBrandId,
      clinicBrandName,
      clinicId,
      eventTypesCsv,
      patientId,
      practitionerId,
      resolvedEventType,
      targetRole,
    ],
  )

  useEffect(() => {
    let active = true

    async function loadCapabilities() {
      if (!patientId && !practitionerId) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }
      if (requiresEventTypeSelection) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }

      setLoading(true)
      const result = await getSupportAgentContactCapabilities(context)
      if (!active) {
        return
      }

      if (result?.ok) {
        setCapabilities(result)
        setCapabilitiesError('')
      } else {
        setCapabilities(null)
        setCapabilitiesError(result?.message || 'Unable to load contact capabilities.')
      }
      setLoading(false)
    }

    loadCapabilities()

    return () => {
      active = false
    }
  }, [context, patientId, practitionerId, requiresEventTypeSelection])

  async function refreshCapabilities() {
    if (!patientId && !practitionerId) {
      return null
    }
    const result = await getSupportAgentContactCapabilities(context)
    if (result?.ok) {
      setCapabilities(result)
      setCapabilitiesError('')
      return result
    }
    setCapabilities(null)
    setCapabilitiesError(result?.message || 'Unable to load contact capabilities.')
    return result
  }

  async function loadWhatsappThread() {
    setThreadLoading(true)
    setThreadError('')
    const result = await getSupportAgentWhatsappThread(context)
    if (result?.ok) {
      setThreadState({
        messages: result?.messages || [],
        templateActions: result?.templateActions || [],
        freeFormAllowed: !!result?.freeFormAllowed,
        targetDisplayName: result?.targetDisplayName || capabilities?.targetDisplayName || '',
      })
      setSelectedTemplateAction((currentValue) => {
        const availableTemplateIds = (result?.templateActions || []).map((item) => item?.id)
        if (currentValue && availableTemplateIds.includes(currentValue)) {
          return currentValue
        }
        return result?.templateActions?.[0]?.id || ''
      })
    } else {
      setThreadState({
        messages: [],
        templateActions: [],
        freeFormAllowed: false,
        targetDisplayName: capabilities?.targetDisplayName || '',
      })
      setThreadError(result?.message || 'Unable to load the WhatsApp conversation.')
    }
    setThreadLoading(false)
    return result
  }

  async function openWhatsappDrawer() {
    setWhatsappDrawerOpen(true)
    await loadWhatsappThread()
  }

  async function handleSendTemplate() {
    if (!selectedTemplateAction) {
      toast.error('Choose a template action first.')
      return
    }

    setTemplateSending(true)
    const result = await sendSupportAgentWhatsappTemplate(context, selectedTemplateAction)
    setTemplateSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp template.')
      return
    }

    toast.success('Template sent on WhatsApp.')
    await Promise.all([refreshCapabilities(), loadWhatsappThread()])
  }

  async function handleSendMessage() {
    if (!messageDraft.trim()) {
      toast.error('Enter a message first.')
      return
    }

    setMessageSending(true)
    const result = await sendSupportAgentWhatsappMessage(context, messageDraft.trim())
    setMessageSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp message.')
      return
    }

    toast.success('WhatsApp message sent.')
    setMessageDraft('')
    await Promise.all([refreshCapabilities(), loadWhatsappThread()])
  }

  async function handlePrepareEmail() {
    setEmailLoading(true)
    const result = await prepareSupportAgentEmail(context)
    setEmailLoading(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to prepare the email draft.')
      return
    }

    const mailtoUri = result?.data?.mailtoUri
    if (!mailtoUri) {
      toast.error('Email draft URL was not returned.')
      return
    }
    window.location.href = mailtoUri
  }

  const channels = capabilities?.channels || {}
  const phoneChannel = channels?.phone || {}
  const whatsappChannel = channels?.whatsapp || {}
  const emailChannel = channels?.email || {}
  const phoneProviderBadge =
    phoneChannel?.providerType === 'plivo' ? '(P)' : phoneChannel?.providerType === 'twilio' ? '(T)' : ''

  const disableAllActions = loading || requiresEventTypeSelection
  const selectionMessage = requiresEventTypeSelection ? 'Choose an event category first.' : ''
  const canCall = !!phoneChannel?.enabled && !disableAllActions
  const canWhatsapp = !!whatsappChannel?.enabled && !disableAllActions
  const canEmail = !!emailChannel?.enabled && !disableAllActions
  const hasAnyVisibleAction = canCall || canWhatsapp || canEmail

  if (
    compact &&
    !title &&
    !loading &&
    !capabilitiesError &&
    !selectionMessage &&
    !hasAnyVisibleAction
  ) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          p: compact ? 1.25 : 1.5,
          borderRadius: 2,
          border: '1px solid rgba(59, 130, 246, 0.14)',
          background:
            'linear-gradient(135deg, rgba(239, 246, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
          ...sx,
        }}
      >
        {title ? (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: '#1d4ed8', mb: compact ? 1 : 1.25 }}
          >
            {title}
          </Typography>
        ) : null}

        {!eventType && !eventTypesCsv ? (
          <TextField
            select
            fullWidth
            size="small"
            label="Support Category"
            value={selectedEventType}
            onChange={(event) => setSelectedEventType(event.target.value)}
            sx={{ mb: compact ? 1 : 1.25 }}
          >
            {normalizedEventTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: compact ? 1 : 1.25 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Checking allowed contact modes...
            </Typography>
          </Box>
        ) : null}

        {capabilitiesError ? (
          <Alert severity="warning" sx={{ mb: compact ? 1 : 1.25 }}>
            {capabilitiesError}
          </Alert>
        ) : null}

        {!capabilitiesError && selectionMessage ? (
          <Alert severity="info" sx={{ mb: compact ? 1 : 1.25 }}>
            {selectionMessage}
          </Alert>
        ) : null}

        {!loading && !capabilitiesError && !selectionMessage && !hasAnyVisibleAction ? (
          <Alert severity="info" sx={{ mb: compact ? 0 : 1.25 }}>
            No usable contact channel is currently available for this record.
          </Alert>
        ) : null}

        {hasAnyVisibleAction ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canCall ? (
              <PortalVoiceCallButton
                actorRole="support-agent"
                patientId={patientId}
                practitionerId={practitionerId}
                bookedEventId={bookedEventId}
                clinicBrandId={clinicBrandId}
                clinicId={clinicId}
                targetRole={targetRole}
                eventType={resolvedEventType || undefined}
                eventTypesCsv={eventTypesCsv || undefined}
                uiSurface={uiSurface}
                allowedDestinationTypes={[phoneChannel?.destinationType]}
                autoStartWhenSingleRoute
                buttonLabel="Call"
                size="small"
              />
            ) : null}

            {canWhatsapp ? (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={openWhatsappDrawer}
              >
                WhatsApp
              </Button>
            ) : null}

            {canEmail ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon />}
                onClick={handlePrepareEmail}
                disabled={emailLoading}
              >
                Email
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {phoneChannel?.outcallNumber && canCall ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Calls from this page use outcall number {phoneChannel.outcallNumber}
            {phoneProviderBadge ? ` ${phoneProviderBadge}` : ''}.
          </Typography>
        ) : null}
      </Box>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={whatsappDrawerOpen}
        onClose={() => {
          if (!threadLoading && !templateSending && !messageSending) {
            setWhatsappDrawerOpen(false)
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 520,
            maxWidth: '100%',
            height: isMobile ? '86vh' : '100%',
            borderTopLeftRadius: isMobile ? 20 : 0,
            borderTopRightRadius: isMobile ? 20 : 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background:
              'linear-gradient(180deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 36%)',
          }}
        >
          <Box sx={{ px: 2.25, py: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    WhatsApp Thread
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {threadState?.targetDisplayName || capabilities?.targetDisplayName || 'Recipient'}
                  </Typography>
                </Box>
                <Button onClick={() => setWhatsappDrawerOpen(false)}>Close</Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(capabilities?.resolvedEventTypes || []).map((item) => (
                  <Chip key={item} size="small" label={item} variant="outlined" />
                ))}
                {threadState?.freeFormAllowed ? (
                  <Chip size="small" color="success" label="Free-form open" />
                ) : (
                  <Chip size="small" color="warning" label="Template first" />
                )}
              </Stack>
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ px: 2.25, py: 1.5 }}>
            {threadState?.freeFormAllowed ? (
              <Alert severity="success">
                The recipient has replied recently, so you can continue with free-form WhatsApp messages.
              </Alert>
            ) : (
              <Alert severity="info">
                Start with an approved template. Free-form WhatsApp opens after the recipient replies within the 24-hour window.
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              px: 2.25,
              pb: 2,
              overflowY: 'auto',
            }}
          >
            {threadLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : threadError ? (
              <Alert severity="warning">{threadError}</Alert>
            ) : (
              <Stack spacing={1.25}>
                {threadState?.messages?.length > 0 ? (
                  threadState.messages.map((item) => <MessageBubble key={item?.id || `${item?.createdAt}-${item?.actionType}`} item={item} />)
                ) : (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px dashed rgba(148, 163, 184, 0.4)',
                      background: 'rgba(248, 250, 252, 0.9)',
                      px: 2,
                      py: 3,
                      textAlign: 'center',
                    }}
                  >
                    <HistoryEduRoundedIcon sx={{ fontSize: 30, color: '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      No WhatsApp interactions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start with the approved template below so the conversation has a clean first message.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box sx={{ px: 2.25, py: 2, display: 'grid', gap: 1.5 }}>
            {threadState?.templateActions?.length > 0 ? (
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid rgba(34, 197, 94, 0.16)',
                  background: 'rgba(240, 253, 244, 0.8)',
                  px: 1.5,
                  py: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534', mb: 1 }}>
                  Approved template
                </Typography>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={1}
                  alignItems={isMobile ? 'stretch' : 'flex-end'}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Template action"
                    value={selectedTemplateAction}
                    onChange={(event) => setSelectedTemplateAction(event.target.value)}
                  >
                    {threadState.templateActions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSendTemplate}
                    disabled={!selectedTemplateAction || templateSending}
                    startIcon={templateSending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
                    sx={{ minWidth: isMobile ? '100%' : 144 }}
                  >
                    Send template
                  </Button>
                </Stack>

                {selectedTemplateAction ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {threadState.templateActions.find((item) => item.id === selectedTemplateAction)?.description}
                  </Typography>
                ) : null}
              </Box>
            ) : !threadState?.freeFormAllowed ? (
              <Alert severity="warning">
                No approved WhatsApp template is configured for this contact yet, so outbound WhatsApp cannot be initiated from here.
              </Alert>
            ) : null}

            {threadState?.freeFormAllowed ? (
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid rgba(59, 130, 246, 0.16)',
                  background: 'rgba(239, 246, 255, 0.82)',
                  px: 1.5,
                  py: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1d4ed8', mb: 1 }}>
                  Send free-form message
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Message"
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  placeholder="Type the next WhatsApp message..."
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.25 }}>
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={messageSending}
                    startIcon={messageSending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
                  >
                    Send message
                  </Button>
                </Stack>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
